import sellerSetupAndroid from "../assets/screenshots/step-seller-setup-android.webp";
import sellerOrdersIos from "../assets/screenshots/step-seller-orders-ios.webp";
import sellerDashboardAndroid from "../assets/screenshots/step-seller-dashboard-android.webp";
import buyerSearchAndroid from "../assets/screenshots/step-buyer-search-android.webp";
import buyerFollowAndroid from "../assets/screenshots/step-buyer-follow-android.webp";
import buyerReviewsIos from "../assets/screenshots/step-buyer-reviews-ios.webp";

// Every step carries its own screenshot: the section pins in place and swaps
// screenshot + copy together as you scroll through the steps, so a per-group
// screenshot would leave the visual static across three different claims.
//
// Platforms are picked against the rest of the page, not alternated within
// this list. Where Hero or FeatureGrid already shows the same screen, this
// section uses the *other* platform's capture, which reads as range rather
// than repetition (the bezel radius, platform badge and status bar all
// differ). Sources and framing notes for each capture:
// dev-docs/plans/landing-screenshot-reshoots.md.
export const sellerSteps = [
  {
    step: 1,
    title: "Open your shop in minutes",
    description:
      "List your products, set your pickup or delivery options, and start selling. No complicated setup required.",
    screenshot: {
      src: sellerSetupAndroid,
      alt: "Setting up a new shop in the Ngwaahia app",
      platform: "android",
    },
  },
  {
    step: 2,
    title: "Manage orders with confidence",
    description:
      "Answer offers, track every order from payment to pickup or delivery, verify handoffs with OTP, and keep your inventory in sync automatically.",
    screenshot: {
      src: sellerOrdersIos,
      alt: "Shop owner's order management screen showing a Confirm Payment Received action and pickup OTP entry",
      platform: "ios",
    },
  },
  {
    step: 3,
    title: "Grow a loyal following",
    description:
      "Watch your revenue grow as buyers follow your shop, rate their orders, and come back for more.",
    screenshot: {
      src: sellerDashboardAndroid,
      alt: "Shop owner dashboard showing followers, rating and revenue in the Ngwaahia app",
      platform: "android",
    },
  },
];

export const buyerSteps = [
  {
    step: 1,
    title: "Find what you're looking for",
    description:
      "Search by name, filter by category or delivery method, and sort by what matters most to you.",
    screenshot: {
      src: buyerSearchAndroid,
      alt: "Search results with category and delivery filters in the Ngwaahia app",
      platform: "android",
    },
  },
  {
    step: 2,
    title: "Buy confidently with real reviews",
    description:
      "Make informed choices by checking feedback from other buyers before you checkout.",
    screenshot: {
      src: buyerReviewsIos,
      alt: "Shop reviews with ratings and buyer photos in the Ngwaahia app",
      platform: "ios",
    },
  },
  {
    step: 3,
    title: "Follow your favorites",
    description:
      "Follow the shops and people you trust, and their new listings surface first in your feed.",
    screenshot: {
      src: buyerFollowAndroid,
      alt: "A shop storefront with its follow button in the Ngwaahia app",
      platform: "android",
    },
  },
];

// Seller first, matching the order the section is written to be read in.
export const stepGroups = [
  { key: "seller", label: "For Sellers", steps: sellerSteps },
  { key: "buyer", label: "For Buyers", steps: buyerSteps },
];

// Both groups flattened into the single ordered list the desktop scroll
// sequence walks through, each entry tagged with the group it belongs to so
// the tab highlight can follow along.
export const flatSteps = stepGroups.flatMap((group) =>
  group.steps.map((step) => ({ ...step, groupKey: group.key })),
);

// Index of each group's first step, so clicking a tab can jump straight to it.
export const groupStartIndex = stepGroups.reduce((acc, group) => {
  acc[group.key] = flatSteps.findIndex((s) => s.groupKey === group.key);
  return acc;
}, {});
